import removeMarkdown from '@brainfish-ai/remove-markdown';

/**
 * Same-length normalization applied to each PM text node when building the flat
 * doc string. Keeps `offsetToPos` aligned with `flat` (no character deletion).
 *
 * Do not strip zero-width characters here — `normalizeForAnchorMatch` strips them
 * on API anchors only; removing them from flat would desync `offsetToPos` lengths.
 */
export function normalizeFlatTextChunk(text: string): string {
  return text
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/\u00A0/g, ' ');
}

/**
 * Normalization for API anchor fields after markdown strip. May remove zero-width
 * characters that do not appear in flattened editor text.
 */
export function normalizeForAnchorMatch(text: string): string {
  return normalizeFlatTextChunk(text).replace(/[\u200B-\u200D\uFEFF]/g, '');
}

/**
 * Trailing whitespace is significant for anchor matching; removeMarkdown trims non-trailing space.
 * Newlines are stripped from the final string (including trailing `\s` that is only `\r`/`\n`) so
 * anchors align with `flattenDoc`, which does not insert block newlines between text nodes.
 */
export function stripMdKeepTrail(text: string): string {
  const trailingWhitespace = text.match(/\s*$/)?.[0] ?? '';
  const head = text.slice(0, text.length - trailingWhitespace.length);
  const headNoNewlines = head.replace(/\r?\n/g, '');
  const merged = removeMarkdown(headNoNewlines).replace(/\s*$/, '') + trailingWhitespace;

  // Remove trailing new lines from the merged string
  return merged.replace(/\r?\n/g, '');
}

/**
 * Normalize API anchor fields (may contain markdown) for matching against the
 * resolver's flattened document string: strip markdown/newlines, then apply
 * `normalizeForAnchorMatch`. Platform passes stored anchors unchanged;
 * this runs in `resolveReviewSuggestionRanges` before anchor location.
 */
export function normalizeAnchors(
  originalText: string,
  contextPrefix: string,
  contextSuffix: string,
): { originalText: string; contextPrefix: string; contextSuffix: string } {
  return {
    originalText: normalizeForAnchorMatch(stripMdKeepTrail(originalText)),
    contextPrefix: normalizeForAnchorMatch(stripMdKeepTrail(contextPrefix)),
    contextSuffix: normalizeForAnchorMatch(stripMdKeepTrail(contextSuffix)),
  };
}
